import { Controller } from '@hotwired/stimulus';

// Matches $ui-side-drawer-breakpoint: below it the drawer is full-width, so
// pushing would squeeze the page content to nothing.
const PUSH_MIN_WIDTH = 768;
const PUSH_DURATION = 250;

export default class extends Controller {
  static targets = ['drawer'];

  static values = { pushSelector: String };

  connect() {
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
    document.addEventListener('click', this.onTriggerClick);
    if (this.isOpen) this.acquireOpenState();
  }

  disconnect() {
    document.removeEventListener('click', this.onTriggerClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    this.resetPush({ animated: false });
  }

  open() {
    if (this.isOpen) return;
    this.previousFocus = document.activeElement;
    this.element.classList.add('ui-side-drawer-viewport--open');
    if (this.hasDrawerTarget) this.drawerTarget.setAttribute('aria-hidden', 'false');
    this.acquireOpenState();
    this.dispatch('open');
  }

  close() {
    if (!this.isOpen) return;
    this.element.classList.remove('ui-side-drawer-viewport--open');
    if (this.hasDrawerTarget) this.drawerTarget.setAttribute('aria-hidden', 'true');
    this.resetPush();
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') this.previousFocus.focus();
    this.dispatch('close');
  }

  get isOpen() {
    return this.element.classList.contains('ui-side-drawer-viewport--open');
  }

  acquireOpenState() {
    this.applyPush();
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);
    requestAnimationFrame(() => {
      if (this.hasDrawerTarget) this.drawerTarget.focus();
    });
  }

  applyPush() {
    if (!this.hasDrawerTarget) return;
    if (!this.canPush) {
      this.resetPush({ animated: false });
      return;
    }

    const target = this.pushedElement || this.resolvedPushElement;
    if (!target) return;

    if (!this.pushedElement) {
      this.pushedElement = target;
      target.style.transition = `margin ${PUSH_DURATION}ms ease`;
      target.style.marginLeft = getComputedStyle(target).marginLeft;
    }
    target.style.marginRight = `${this.drawerTarget.offsetWidth}px`;
  }

  resetPush({ animated = true } = {}) {
    const target = this.pushedElement;
    if (!target) return;

    this.pushedElement = null;
    target.style.marginLeft = '';
    target.style.marginRight = '';
    clearTimeout(this.pushCleanupTimer);
    if (animated) {
      this.pushCleanupTimer = setTimeout(() => { target.style.transition = ''; }, PUSH_DURATION);
    } else {
      target.style.transition = '';
    }
  }

  get canPush() {
    return window.innerWidth > PUSH_MIN_WIDTH;
  }

  get resolvedPushElement() {
    if (!this.pushSelectorValue) return document.body;

    const target = document.querySelector(this.pushSelectorValue);
    if (!target) {
      console.warn(`[${this.identifier}] push selector "${this.pushSelectorValue}" matched no element`); // eslint-disable-line no-console
    }
    return target || document.body;
  }

  onResize() {
    if (!this.isOpen) return;
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.applyPush(), 100);
  }

  onTriggerClick(event) {
    const trigger = event.target.closest(`[data-side-drawer-trigger="${this.element.id}"]`);
    if (!trigger) return;
    event.preventDefault();
    this.open();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }
}
